<script>
  import { format } from 'svelte-i18n';
  import { Spinner, Tooltip } from '@sveltestrap/sveltestrap';

  import { ActivityUserAgents } from '@client/components/activity-user-agents';
  import { Icon } from '@client/components/icon';

  import { MessagesService } from '@client/services/messages';

  import { getFlag } from '@client/utils/flags';

  import { generateUUID } from '@common/utils/strings';

  /**
   * @typedef {Object} ActivityNote
   * @property {string} [isp] - Internet Service Provider.
   * @property {string} [country] - Country of origin.
   * @property {string} [location] - User's location string.
   * @property {string} [ua] - User agent string.
   */

  /**
   * @typedef {Object} ActivityEntry
   * @property {string} [ip] - IP address.
   * @property {ActivityNote} note - Metadata related to user device and location.
   */

  /**
   * A single activity history entry.
   * Must contain `ip` and a `note` object with ISP, country, location, and user agent.
   * @type {ActivityEntry}
   */
  export let entry;

  /**
   * Whether to allow reporting of suspicious activity.
   * @type {boolean}
   */
  export let allowReporting = false;

  /**
   * The Internet Service Provider name from the entry note.
   * @type {string | undefined}
   */
  $: isp = entry.note.isp;

  /**
   * The IP address associated with the entry.
   * @type {string | undefined}
   */
  $: ip = entry.ip;

  /**
   * A CSS class name for a flag icon based on the user's country.
   * Defaults to 'United States' if not specified.
   * @type {string}
   */
  $: flag = getFlag(entry.note.country || 'United States');

  /**
   * Indicates whether the entry has been reported as a scanner.
   * @type {boolean}
   */
  let reported = false;

  /**
   * Indicates whether a report is currently being submitted.
   * @type {boolean}
   */
  let reporting = false;

  /**
   * Reports the current activity entry as a scanner.
   * Sets `reporting` to true while the report is in progress,
   * and `reported` to true once the report is complete.
   * Catches and logs any errors that occur during the reporting process.
   * @returns {Promise<void>}
   */
  const reportScanner = async () => {
    reporting = true;

    try {
      await MessagesService.report(entry);
    } catch (error) {
      console.log('Error reporting scanner:', error);
    }

    reporting = false;
    reported = true;
  };
</script>

<div>
  <small>
    <ActivityUserAgents ua={entry.note.ua} />
    <span class="location">
      <i class={flag} />
      <span>{entry.note?.location?.replace(/-, -/g, '') || 'United States'}</span>
      <span>
        {#if isp}
          {$format('label.ISP', { values: { isp } })}
        {/if}
        {#if ip}
          {$format('label.IP', { values: { ip } })}
        {/if}
      </span>
    </span>
    {#if allowReporting}
      <span class="report-flag" on:click={reportScanner} role="button" tabindex="0">
        {#if reporting}
          <Spinner size="sm" color="primary" />
        {:else}
          {@const uuid = `report-scanner-${generateUUID()}`}
          <Icon name="flag" class="pointer d-inline-block {reported ? 'reported' : ''}" style="z-index: 10" id={uuid} />
          <Tooltip target={uuid} placement="right">
            {$format('label.TOOLTIP.REPORT_SCANNER')}
          </Tooltip>
        {/if}
      </span>
    {/if}
  </small>
</div>

<style lang="scss" src="./item-location.scss"></style>
